import React from "react";
import {
  Tab as RACTab,
  type TabListProps as RACTabListProps,
  TabPanel as RACTabPanel,
  type TabPanelProps as RACTabPanelProps,
  type TabProps as RACTabProps,
  Tabs as RACTabs,
  TabList as RACTabsList,
  type TabsProps as RACTabsProps,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import { composeTailwindRenderProps, focusRing } from "./utils";

const tabListStyles = tv({
  base: "inline-flex items-center justify-center rounded-md bg-muted p-0.5 text-muted-foreground/70",
  variants: {
    variant: {
      segment: "max-h-8",
      underline: "",
      pills: "gap-1 bg-transparent",
    },
    orientation: {
      horizontal: "flex-row overflow-x-auto",
      vertical: "flex-col max-h-none",
    },
  },
  compoundVariants: [
    {
      variant: "underline",
      orientation: "vertical",
      className:
        "flex-col rounded-none border-l border-border bg-transparent p-0 px-1 py-0",
    },
    {
      variant: "underline",
      orientation: "horizontal",
      className:
        "h-auto gap-2 rounded-none border-b border-border bg-transparent px-0 py-1 text-foreground",
    },
  ],
  defaultVariants: {
    variant: "underline",
    orientation: "horizontal",
  },
});

const tabStyles = tv({
  extend: focusRing,
  base: [
    "inline-flex items-center justify-center cursor-default whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium hover:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 data-[selected=true]:bg-background data-[selected=true]:text-foreground data-[selected=true]:shadow-xs data-[selected=true]:shadow-black/5",
    "[&_svg]:shrink-0 [&_svg]:opacity-50 [&_svg]:text-current [&_svg]:stroke-current [&_svg:not([class*=size-])]:size-4 [&_svg]:me-1.5 [&_svg]:-ms-1",
  ],
  variants: {
    variant: {
      segment: "py-1 rounded-sm data-[selected=true]:hover:text-foreground",
      underline: "",
      pills:
        "py-1 data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground data-[selected=true]:shadow-none data-[selected=true]:hover:text-primary-foreground",
    },
    orientation: {
      horizontal: "w-auto",
      vertical: "w-full",
    },
  },
  compoundVariants: [
    {
      variant: "underline",
      orientation: "horizontal",
      className:
        "relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1 after:h-0.5 hover:bg-accent hover:text-foreground data-[selected=true]:bg-transparent data-[selected=true]:shadow-none data-[selected=true]:after:bg-primary data-[selected=true]:hover:bg-accent data-[selected=true]:hover:text-foreground",
    },
    {
      variant: "underline",
      orientation: "vertical",
      className:
        "relative w-full justify-start after:absolute after:inset-y-0 after:start-0 after:-ms-1 after:w-0.5 hover:bg-accent hover:text-foreground data-[selected=true]:bg-transparent data-[selected=true]:shadow-none data-[selected=true]:after:bg-primary data-[selected=true]:hover:bg-accent data-[selected=true]:hover:text-foreground",
    },
  ],
  defaultVariants: {
    variant: "underline",
    orientation: "horizontal",
  },
});

const tabPanelStyles = tv({
  base: "mt-2 outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70",
});

type TabsContextValue = VariantProps<typeof tabListStyles>;

const TabsContext = React.createContext<TabsContextValue>({
  variant: "underline",
});

type TabsProps = RACTabsProps &
  TabsContextValue & { ref?: React.RefObject<HTMLDivElement> };

export function Tabs({ variant, ref, orientation, ...props }: TabsProps) {
  return (
    <TabsContext.Provider value={{ variant, orientation }}>
      <RACTabs {...props} ref={ref} orientation={orientation} />
    </TabsContext.Provider>
  );
}

type TabListProps<T> = RACTabListProps<T> & {
  ref?: React.RefObject<HTMLDivElement>;
};

export function TabsList<T extends object>({
  className,
  ref,
  ...props
}: TabListProps<T>) {
  const { variant, orientation } = React.useContext(TabsContext);
  return (
    <RACTabsList
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        className,
        tabListStyles({
          variant,
          orientation,
        }),
      )}
    />
  );
}

type TabProps = RACTabProps & { ref?: React.RefObject<HTMLDivElement> };

export function Tab({ className, ref, ...props }: TabProps) {
  const { variant, orientation } = React.useContext(TabsContext);
  return (
    <RACTab
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        className,
        tabStyles({
          variant,
          orientation,
        }),
      )}
    />
  );
}

type TabPanelProps = RACTabPanelProps & {
  ref?: React.RefObject<HTMLDivElement>;
};

export function TabPanel({ className, ref, ...props }: TabPanelProps) {
  return (
    <RACTabPanel
      {...props}
      ref={ref}
      className={tabPanelStyles({ className: className as string })}
    />
  );
}
